import React, { createContext, useContext, useState, useCallback } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle, AlertTriangle, XCircle, Info, X } from 'lucide-react';

const MessageContext = createContext(null);

export const useMessage = () => useContext(MessageContext);

export const MessageProvider = ({ children }) => {
  const [messages, setMessages] = useState([]);

  const removeMessage = useCallback((id) => {
    setMessages((prev) => prev.filter((msg) => msg.id !== id));
  }, []);

  const showMessage = useCallback((type, content, duration = 3000) => {
    const id = Date.now().toString() + Math.random().toString(36).substr(2, 9);

    setMessages((prev) => {
      const current = prev.length >= 3 ? prev.slice(1) : prev;
      return [...current, { id, type, content }];
    });

    setTimeout(() => {
      removeMessage(id);
    }, duration);
  }, [removeMessage]);

  return (
    <MessageContext.Provider value={{ showMessage }}>
      {children}
      {/* 固定右上角，不遮挡中间模态框 */}
      <div className="fixed top-5 right-5 z-[9999] flex flex-col gap-2.5 pointer-events-none items-end" style={{ maxWidth: 380 }}>
        <AnimatePresence>
          {messages.map((msg) => (
            <MessageItem key={msg.id} message={msg} onRemove={removeMessage} />
          ))}
        </AnimatePresence>
      </div>
    </MessageContext.Provider>
  );
};

/* ── 通知颜色配置 ── */
const TOAST_CONFIG = {
  success: {
    bg: 'bg-white',
    border: 'border-emerald-200',
    iconBg: 'bg-emerald-50',
    iconColor: 'text-emerald-500',
    textColor: 'text-gray-800',
    icon: CheckCircle,
    barColor: 'bg-emerald-400',
  },
  warning: {
    bg: 'bg-white',
    border: 'border-amber-200',
    iconBg: 'bg-amber-50',
    iconColor: 'text-amber-500',
    textColor: 'text-gray-800',
    icon: AlertTriangle,
    barColor: 'bg-amber-400',
  },
  error: {
    bg: 'bg-white',
    border: 'border-red-200',
    iconBg: 'bg-red-50',
    iconColor: 'text-red-500',
    textColor: 'text-gray-800',
    icon: XCircle,
    barColor: 'bg-red-400',
  },
  info: {
    bg: 'bg-white',
    border: 'border-blue-200',
    iconBg: 'bg-blue-50',
    iconColor: 'text-blue-500',
    textColor: 'text-gray-800',
    icon: Info,
    barColor: 'bg-blue-400',
  },
};

const MessageItem = ({ message, onRemove }) => {
  const { type, content, id } = message;
  const config = TOAST_CONFIG[type] || TOAST_CONFIG.success;
  const IconComp = config.icon;

  return (
    <motion.div
      initial={{ opacity: 0, x: 60, scale: 0.95 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: 40, scale: 0.95, transition: { duration: 0.2 } }}
      layout
      className={`pointer-events-auto w-full min-w-[280px] max-w-[380px] ${config.bg} rounded-xl shadow-lg shadow-gray-900/8 border ${config.border} overflow-hidden`}
    >
      {/* 顶部彩色指示条 */}
      <div className={`h-0.5 ${config.barColor}`} />

      <div className="flex items-start gap-3 px-4 py-3">
        {/* 图标 */}
        <div className={`flex-shrink-0 w-7 h-7 rounded-lg ${config.iconBg} flex items-center justify-center mt-0.5`}>
          <IconComp className={`w-4 h-4 ${config.iconColor}`} />
        </div>

        {/* 文本 */}
        <div className={`flex-1 text-sm font-medium ${config.textColor} leading-relaxed pt-0.5 break-words`}>
          {content}
        </div>

        {/* 关闭按钮 */}
        <button
          onClick={() => onRemove(id)}
          className="flex-shrink-0 w-6 h-6 rounded-md flex items-center justify-center text-gray-300 hover:text-gray-500 hover:bg-gray-100 transition-all mt-0.5"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </motion.div>
  );
};
